import { useState, type FormEvent } from 'react';
import { useAuth } from '../lib/auth.tsx';
import { Button } from '../components/ui/index.ts';

/**
 * One password field, no username. ADMIN_PASSWORD grants full access,
 * GUEST_PASSWORD grants Adam's read-only view; both set the same signed
 * httpOnly cookie and differ only in the role it carries.
 */
export default function Login() {
  const { signIn } = useAuth();
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      await signIn(password);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Could not sign in.');
      setPassword('');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center px-6">
      <div className="w-full max-w-xs">
        <h1 className="text-lg font-semibold tracking-tighter">Sales Intelligence</h1>
        <p className="mt-1 text-xs text-fg-subtle">Internal tool. Sign in to continue.</p>

        <form onSubmit={onSubmit} className="mt-6 space-y-3">
          <div>
            <label htmlFor="password" className="sr-only">
              Password
            </label>
            <input
              id="password"
              type="password"
              autoFocus
              autoComplete="current-password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              placeholder="Password"
              className="w-full rounded-md border border-border bg-surface px-3 py-2 text-sm text-fg placeholder:text-fg-faint focus:border-border-strong"
            />
          </div>

          {error && <p className="text-xs text-lost">{error}</p>}

          <Button
            type="submit"
            variant="primary"
            fullWidth
            loading={submitting}
            disabled={password === ''}
          >
            Sign in
          </Button>
        </form>
      </div>
    </div>
  );
}
